import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Runbook de operaciones para el equipo super_admin de MiPueblo.
 * Cubre todo lo que se necesita para mantener viva la plataforma:
 * arquitectura, deploys, backups, incidentes comunes, secrets.
 *
 * Pensado para que cualquier persona del equipo pueda abrirlo a las
 * 3am durante un incidente y resolver sin tener que despertar a nadie.
 */
export function DocsRunbookPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Runbook de operaciones
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Runbook de operaciones de MiPueblo
        </h1>

        <div className="callout">
          <strong>Tiempo de lectura:</strong> 20-25 min la primera vez. Después
          se usa como referencia rápida cuando algo se rompe en producción.
        </div>

        {/* ---- Arquitectura ---- */}
        <Section title="1. Arquitectura general">
          <p>
            Todo el stack de producción vive en un solo VPS (alias{" "}
            <code>tiza-vps</code>) detrás de Cloudflare y nginx + certbot. Los
            servicios corren en una red Docker aislada{" "}
            <code>mipueblo-net</code>. Postgres NO está expuesto al host —
            solo se accede vía la red docker.
          </p>
          <Table
            head={["Servicio", "Container", "Imagen", "Función"]}
            rows={[
              [
                "Postgres",
                "mipueblo-postgres",
                "postgres:16-alpine",
                "Base de datos única con RLS por tenant",
              ],
              [
                "Redis",
                "mipueblo-redis",
                "redis:7-alpine",
                "Cola del worker ARQ + caché ligero",
              ],
              [
                "API",
                "mipueblo-api",
                "mipueblo-api:latest",
                "FastAPI en :8000 (mapeado a 127.0.0.1:8081)",
              ],
              [
                "Worker",
                "mipueblo-worker",
                "mipueblo-api:latest",
                "ARQ: envíos WhatsApp, jobs diferidos",
              ],
              [
                "Panel (super-admin)",
                "—",
                "estático en nginx",
                "Vite build sirvido desde /var/www/panel",
              ],
              [
                "Tenant-app",
                "—",
                "estático en nginx",
                "SPA del subdominio {slug}.mipuebloapp.com",
              ],
              [
                "Nginx + certbot",
                "host",
                "—",
                "Reverse proxy + TLS (Let's Encrypt)",
              ],
            ]}
          />
          <p style={{ marginTop: 12 }}>
            Definición canónica:{" "}
            <code>/opt/mipueblo/api/docker-compose.prod.yml</code>.
          </p>
        </Section>

        {/* ---- SSH ---- */}
        <Section title="2. Acceso al server">
          <p>
            Solo el equipo super_admin tiene SSH. Llaves rotadas cada 6 meses.
          </p>
          <pre className="code-block">{`ssh tiza-vps                      # alias en ~/.ssh/config
# usuario por defecto: deploy
# sudo está limitado a docker + systemctl + journalctl`}</pre>
          <p>
            Si <code>ssh tiza-vps</code> no resuelve, agrega al{" "}
            <code>~/.ssh/config</code>:
          </p>
          <pre className="code-block">{`Host tiza-vps
  HostName <IP del VPS>
  User deploy
  IdentityFile ~/.ssh/mipueblo_deploy`}</pre>
          <div className="callout warn">
            <strong>Nunca</strong> compartas la llave privada por Slack/email.
            Si alguien deja el equipo, rotar las llaves del server el mismo
            día.
          </div>
        </Section>

        {/* ---- CI/CD ---- */}
        <Section title="3. CI/CD — cómo deploya cada push a main">
          <p>
            Cada push a <code>main</code> dispara un workflow de GitHub
            Actions según los <code>paths</code> tocados:
          </p>
          <Table
            head={["Path tocado", "Workflow", "Qué hace"]}
            rows={[
              [
                "apps/backend/**",
                "deploy-backend.yml",
                "Lint + mypy + pytest → rsync al server → docker compose up -d --build → health check",
              ],
              [
                "apps/panel/**",
                "deploy-panel.yml",
                "Build Vite + rsync a /var/www/panel",
              ],
              [
                "apps/tenant-app/**",
                "deploy-tenant-app.yml",
                "Build Vite + rsync a /var/www/tenant-app",
              ],
            ]}
          />
          <p>
            El CI corre contra un Postgres efímero, crea el rol{" "}
            <code>mipueblo</code> NOSUPERUSER (para que RLS aplique de verdad
            en los tests) y corre <code>ruff + mypy + pytest</code>. Si algo
            falla, el deploy NO sucede.
          </p>
          <p>
            Después del rsync, el server ejecuta:
          </p>
          <pre className="code-block">{`cd /opt/mipueblo/api
sudo docker compose -f docker-compose.prod.yml \\
  --env-file .env.deploy up -d --build`}</pre>
          <p>
            El archivo <code>.env.deploy</code> contiene los secrets, viene
            del workflow y se borra inmediatamente después.
          </p>
          <p>
            Verificación final:{" "}
            <code>curl https://api.mipuebloapp.com/health</code> 5 veces
            (10s entre cada intento). Si no responde 200, el deploy queda
            marcado en rojo en GitHub.
          </p>
        </Section>

        {/* ---- Restart ---- */}
        <Section title="4. Restart manual de servicios">
          <p>Cuando hace falta reiniciar sin redeploy (ej. cambio de env):</p>
          <pre className="code-block">{`# Reiniciar solo la API (mantiene worker, db, redis)
sudo docker compose -f /opt/mipueblo/api/docker-compose.prod.yml restart api

# Reiniciar el worker
sudo docker compose -f /opt/mipueblo/api/docker-compose.prod.yml restart worker

# Reiniciar todo el stack (downtime ~10s)
sudo docker compose -f /opt/mipueblo/api/docker-compose.prod.yml restart

# Bajar y volver a levantar (downtime ~20s, recarga imagen)
sudo docker compose -f /opt/mipueblo/api/docker-compose.prod.yml up -d`}</pre>
          <div className="callout tip">
            💡 Postgres y Redis casi nunca necesitan restart manual. Si lo
            haces, baja primero la API y el worker para evitar errores de
            conexión en flight.
          </div>
        </Section>

        {/* ---- Backups ---- */}
        <Section title="5. Backups de la base de datos">
          <p>
            El script <code>/opt/mipueblo/api/scripts/backup-db.sh</code>{" "}
            corre todos los días a las <strong>03:00 America/Bogotá</strong>{" "}
            vía systemd timer (<code>mipueblo-backup.timer</code>).
          </p>
          <Table
            rows={[
              ["Formato", "pg_dump --format=custom + xz -9"],
              ["Directorio local", "/var/lib/mipueblo-backups/"],
              [
                "Nombre",
                "mipueblo-<YYYYMMDD-HHMMSSZ>.dump.xz (chmod 600)",
              ],
              ["Retención local", "14 días (configurable BACKUP_RETENTION)"],
              [
                "Off-site",
                "rclone copy → r2:mipueblo-backups (si BACKUP_S3_TARGET está set)",
              ],
              [
                "Tamaño esperado",
                "~50-200 MB comprimido, crece con #PQRSDFs + adjuntos",
              ],
            ]}
          />
          <p>Inspeccionar el timer:</p>
          <pre className="code-block">{`sudo systemctl status mipueblo-backup.timer
sudo systemctl list-timers --all | grep mipueblo
sudo journalctl -u mipueblo-backup.service --since "2 days ago"`}</pre>
          <p>Disparar un backup manual ahora mismo:</p>
          <pre className="code-block">{`sudo systemctl start mipueblo-backup.service
# o directo:
sudo /opt/mipueblo/api/scripts/backup-db.sh`}</pre>
          <div className="callout warn">
            <strong>Verifica una vez al mes</strong> que los backups estén
            efectivamente en R2 — un backup que nunca se restauró es un
            backup que no existe.
          </div>
        </Section>

        {/* ---- Restore ---- */}
        <Section title="6. Restore desde backup">
          <div className="callout warn">
            Esto <strong>borra</strong> los datos actuales y los reemplaza
            con los del dump. Solo hacerlo después de tomar un backup
            adicional del estado actual (por si necesitas volver).
          </div>
          <Ol>
            <li>
              Identificar el dump a restaurar:
              <pre className="code-block">{`ls -lh /var/lib/mipueblo-backups/`}</pre>
            </li>
            <li>
              Tomar backup del estado actual (paracaídas):
              <pre className="code-block">{`sudo /opt/mipueblo/api/scripts/backup-db.sh`}</pre>
            </li>
            <li>
              Parar la API y el worker (evita escrituras durante el
              restore):
              <pre className="code-block">{`sudo docker compose -f /opt/mipueblo/api/docker-compose.prod.yml stop api worker`}</pre>
            </li>
            <li>
              Descomprimir el dump:
              <pre className="code-block">{`xz -dk /var/lib/mipueblo-backups/mipueblo-<TS>.dump.xz`}</pre>
            </li>
            <li>
              Drop + recreate de la DB (cuidado, hace falta el rol{" "}
              <code>postgres</code>):
              <pre className="code-block">{`sudo docker exec -i mipueblo-postgres psql -U postgres <<SQL
DROP DATABASE IF EXISTS mipueblo;
CREATE DATABASE mipueblo OWNER mipueblo;
SQL`}</pre>
            </li>
            <li>
              Restaurar con pg_restore:
              <pre className="code-block">{`cat /var/lib/mipueblo-backups/mipueblo-<TS>.dump | \\
sudo docker exec -i mipueblo-postgres \\
  pg_restore --no-owner --role=mipueblo -U postgres -d mipueblo`}</pre>
            </li>
            <li>
              Levantar API y worker de nuevo:
              <pre className="code-block">{`sudo docker compose -f /opt/mipueblo/api/docker-compose.prod.yml start api worker`}</pre>
            </li>
            <li>
              Verificar:{" "}
              <code>curl https://api.mipuebloapp.com/health</code> +
              login al panel.
            </li>
          </Ol>
        </Section>

        {/* ---- Alembic ---- */}
        <Section title="7. Migraciones Alembic">
          <p>
            Las migraciones corren <strong>automáticamente al arranque</strong>{" "}
            de la API (script <code>entrypoint.sh</code> del Dockerfile). En
            el 99% de los casos no hay que hacer nada manual.
          </p>
          <p>Comandos útiles cuando hay que tocar manualmente:</p>
          <pre className="code-block">{`# Ver versión actual
sudo docker exec mipueblo-api alembic current

# Ver historial
sudo docker exec mipueblo-api alembic history

# Aplicar todas las migraciones pendientes
sudo docker exec mipueblo-api alembic upgrade head

# Bajar UNA revisión (peligroso — verifica que la migración tenga downgrade)
sudo docker exec mipueblo-api alembic downgrade -1`}</pre>
          <p>
            <strong>Si una migración falla en el deploy:</strong> el container
            entra en restart loop. Lee el log:
          </p>
          <pre className="code-block">{`sudo docker logs mipueblo-api --tail 200 2>&1 | grep -i alembic`}</pre>
          <p>
            La solución casi siempre es: arreglar el archivo de la migración
            en el repo, hacer un commit con el fix, y dejar que CI redeploye.
            <strong> No edites migraciones ya aplicadas</strong> — crea una
            nueva que corrija el estado.
          </p>
        </Section>

        {/* ---- Logs ---- */}
        <Section title="8. Logs">
          <p>Todos los containers loggean a json-file rotado (10MB x 3).</p>
          <pre className="code-block">{`# Últimas 500 líneas de la API
sudo docker logs mipueblo-api --tail 500

# Seguir en vivo
sudo docker logs -f mipueblo-api

# Filtrar errores
sudo docker logs mipueblo-api --since 1h 2>&1 | grep -iE "error|exception|traceback"

# Worker
sudo docker logs mipueblo-worker --tail 200

# Postgres (slow queries, locks)
sudo docker logs mipueblo-postgres --tail 200

# Nginx (acceso, 5xx)
sudo tail -f /var/log/nginx/access.log
sudo tail -f /var/log/nginx/error.log`}</pre>
        </Section>

        {/* ---- Salud ---- */}
        <Section title="9. Métricas de salud del sistema">
          <Table
            head={["Qué chequear", "Cómo", "Umbral"]}
            rows={[
              [
                "API responde",
                "curl https://api.mipuebloapp.com/health",
                "200 OK, <500ms",
              ],
              [
                "Conexiones Postgres",
                "psql ... -c 'SELECT count(*) FROM pg_stat_activity;'",
                "< 25 (pool max 30)",
              ],
              [
                "Disco del VPS",
                "df -h /",
                "< 80% (alertar al 85%)",
              ],
              [
                "Memoria",
                "free -h",
                "Postgres no debería pasar 1GB, API 512MB",
              ],
              [
                "Tamaño DB",
                "psql -c \"SELECT pg_size_pretty(pg_database_size('mipueblo'))\"",
                "Crece ~50MB/mes/tenant activo",
              ],
              [
                "Backups recientes",
                "ls -lt /var/lib/mipueblo-backups/ | head",
                "Último < 25h",
              ],
              [
                "Certificados TLS",
                "sudo certbot certificates",
                "Más de 20 días para vencer",
              ],
            ]}
          />
        </Section>

        {/* ---- Incidentes ---- */}
        <Section title="10. Incidentes comunes">
          <Table
            head={["Síntoma", "Diagnóstico", "Solución"]}
            rows={[
              [
                "Certbot venció / TLS roto",
                "curl al dominio → SSL_ERROR",
                "sudo certbot renew && sudo systemctl reload nginx. Si el renew falla por rate-limit, esperar 1h.",
              ],
              [
                "Disco Postgres lleno",
                "df -h muestra > 90%, logs con 'no space left'",
                "1) Borrar backups locales viejos: find /var/lib/mipueblo-backups -mtime +7 -delete. 2) VACUUM FULL en la DB (downtime). 3) Considerar limpieza de audit_log antiguo.",
              ],
              [
                "Worker caído (jobs encolados crecen)",
                "docker ps no muestra mipueblo-worker o redis LLEN arq:queue > 1000",
                "sudo docker compose restart worker. Revisa log para ver si crashea por mismo job tóxico.",
              ],
              [
                "SSE no fluye (chat ciudadano se ve estático)",
                "Network tab del browser: el stream se corta a los segundos",
                "Suele ser nginx buffering. Verifica que el block tenga proxy_buffering off y X-Accel-Buffering: no. Restart nginx.",
              ],
              [
                "5xx intermitentes en panel",
                "Logs API con 'connection pool exhausted'",
                "Subir DATABASE_POOL_SIZE en docker-compose y restart api. Reportar en backlog para revisar queries N+1.",
              ],
              [
                "WhatsApp deja de entregar",
                "Webhook no llega o token expirado",
                "Validar token en Meta Business Suite. Re-generarlo con system user. Confirmar webhook URL en Meta apunta a api.mipuebloapp.com/webhooks/whatsapp/{tenant_slug}.",
              ],
              [
                "Postgres rechaza conexiones",
                "Logs con 'remaining connection slots are reserved'",
                "ALTER SYSTEM SET max_connections = 200; restart postgres. Investigar pool leaks.",
              ],
            ]}
          />
        </Section>

        {/* ---- Nuevo subdominio ---- */}
        <Section title="11. Añadir el subdominio de un nuevo tenant">
          <p>
            Cuando se crea un tenant con slug <code>cajica</code>, hay que
            asegurar que <code>cajica.mipuebloapp.com</code> resuelva y tenga
            TLS. Tenemos comodín así que normalmente NO hay que tocar DNS.
          </p>
          <Ol>
            <li>
              <strong>DNS:</strong> hay un wildcard{" "}
              <code>*.mipuebloapp.com</code> en Cloudflare apuntando al VPS.
              Solo verifica que <code>{`{slug}`}.mipuebloapp.com</code>{" "}
              responda con un dig.
            </li>
            <li>
              <strong>Nginx:</strong> el server block es genérico — usa{" "}
              <code>server_name ~^(?&lt;slug&gt;[a-z0-9-]+)\.mipuebloapp\.com$</code>{" "}
              y enruta al tenant-app. No hace falta crear bloques nuevos por
              tenant.
            </li>
            <li>
              <strong>Certificado:</strong> ya hay un cert wildcard
              gestionado por certbot con DNS challenge. Renueva solo.
            </li>
            <li>
              Smoke test:{" "}
              <code>curl -I https://{`{slug}`}.mipuebloapp.com</code> → 200.
            </li>
          </Ol>
          <div className="callout tip">
            💡 Si por alguna razón el wildcard no aplica (ej: tenant
            enterprise con dominio propio <code>pqrsdf.cajica.gov.co</code>),
            ahí sí toca crear server block dedicado + cert con DNS challenge.
          </div>
        </Section>

        {/* ---- Rotar secrets ---- */}
        <Section title="12. Rotar secrets">
          <p>
            Los secrets viven en <strong>GitHub Actions Secrets</strong> del
            repo. Se inyectan al server en cada deploy via{" "}
            <code>.env.deploy</code> (efímero).
          </p>
          <Table
            head={["Secret", "Riesgo si se filtra", "Frecuencia rotación"]}
            rows={[
              [
                "KEK_KEY",
                "Permite desencriptar credenciales por tenant (WhatsApp tokens, API keys IA)",
                "Anual o inmediato si hay sospecha",
              ],
              [
                "JWT_SECRET_KEY",
                "Permite firmar tokens válidos de cualquier usuario",
                "Semestral. Rotarlo invalida TODAS las sesiones activas.",
              ],
              [
                "MIPUEBLO_DB_PASSWORD",
                "Acceso completo a la DB (rol app, NOSUPERUSER)",
                "Anual",
              ],
              [
                "POSTGRES_SUPER_PASSWORD",
                "Superuser de Postgres",
                "Anual",
              ],
              [
                "ANTHROPIC_API_KEY (fallback)",
                "Consumo no autorizado del crédito",
                "Inmediato si se filtra. Las keys por tenant viven en DB cifradas con KEK.",
              ],
              [
                "DEPLOY_SSH_KEY",
                "Acceso SSH al VPS desde GitHub",
                "Semestral",
              ],
            ]}
          />
          <p>Procedimiento general para rotar:</p>
          <Ol>
            <li>Generar nuevo valor (openssl rand -hex 32 para keys).</li>
            <li>
              Actualizar en GitHub → Settings → Secrets and variables →
              Actions.
            </li>
            <li>
              Disparar workflow <code>deploy-backend.yml</code> manualmente
              (workflow_dispatch).
            </li>
            <li>
              Validar con health check + login.
            </li>
            <li>
              <strong>KEK_KEY</strong> tiene proceso especial: hay que
              re-encriptar todas las credenciales con la nueva. Hay un
              comando dedicado:
              <pre className="code-block">{`sudo docker exec mipueblo-api python -m mipueblo.scripts.rotate_kek \\
  --old-key "$OLD_KEK" --new-key "$NEW_KEK"`}</pre>
            </li>
          </Ol>
          <div className="callout warn">
            <strong>JWT_SECRET_KEY</strong>: rotarlo desloguea a todos los
            usuarios. Coordina la rotación fuera de horario.
          </div>
        </Section>

        {/* ---- Cierre ---- */}
        <Section title="Contactos de escalación">
          <Ul
            items={[
              "Hosting (VPS): proveedor del servidor (ver factura)",
              "DNS: panel Cloudflare con cuenta ops@mipuebloapp.com",
              "Meta / WhatsApp: soporte vía business.facebook.com (response time 24-72h)",
              "Anthropic: support@anthropic.com (billing y rate limits)",
            ]}
          />
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{ borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)" }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
